import { Vector2 } from "../../core/math/Vector2";

const supportedKeys = new Set([
  "arrowleft", "arrowright", "arrowup", "arrowdown", "a", "d", "w", "s",
]);

export class InputController {
  private readonly pressed = new Set<string>();
  private dragging = false;

  bind(canvas: HTMLCanvasElement, onPointerPosition: (position: Vector2) => void): void {
    window.addEventListener("keydown", (event) => this.setKey(event, true));
    window.addEventListener("keyup", (event) => this.setKey(event, false));
    window.addEventListener("blur", () => this.pressed.clear());

    const position = (event: PointerEvent): Vector2 => {
      const rect = canvas.getBoundingClientRect();
      return new Vector2(event.clientX - rect.left, event.clientY - rect.top);
    };
    canvas.addEventListener("pointerdown", (event) => {
      this.dragging = true;
      canvas.setPointerCapture(event.pointerId);
      onPointerPosition(position(event));
    });
    canvas.addEventListener("pointermove", (event) => {
      if (this.dragging) onPointerPosition(position(event));
    });
    canvas.addEventListener("pointerup", () => { this.dragging = false; });
    canvas.addEventListener("pointercancel", () => { this.dragging = false; });
  }

  direction(): Vector2 {
    return new Vector2(
      Number(this.pressed.has("arrowright") || this.pressed.has("d"))
        - Number(this.pressed.has("arrowleft") || this.pressed.has("a")),
      Number(this.pressed.has("arrowdown") || this.pressed.has("s"))
        - Number(this.pressed.has("arrowup") || this.pressed.has("w")),
    );
  }

  private setKey(event: KeyboardEvent, pressed: boolean): void {
    if (this.isTextEntry(event.target)) return;
    const key = event.key.toLowerCase();
    if (!supportedKeys.has(key)) return;
    event.preventDefault();
    if (pressed) this.pressed.add(key);
    else this.pressed.delete(key);
  }

  private isTextEntry(target: EventTarget | null): boolean {
    const element = target as Partial<HTMLElement> | null;
    if (!element?.tagName) return false;
    const tag = element.tagName.toLowerCase();
    return tag === "input"
      || tag === "textarea"
      || Boolean(element.isContentEditable)
      || (element.closest?.("[contenteditable='true']") ?? null) !== null;
  }
}
